import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { appConfig } from '@/lib/appConfig'
import {
  acceptInvitation,
  createUnit,
  getUserInvitations,
  getUserUnitsByContext,
  setCurrentUnit,
} from '@/modules/units'
import { InvitationsStep } from '@/modules/units/onboarding/InvitationsStep'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'

interface AccountingOnboardingWizardProps {
  userId: string
}

function createSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function AccountingOnboardingWizard({ userId }: AccountingOnboardingWizardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setCurrentUnit: setUnitContext } = useUnit()
  const [officeName, setOfficeName] = useState('')
  const [invitations, setInvitations] = useState<any[]>([])
  const [showInvitations, setShowInvitations] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getUserInvitations()
      .then((items) => {
        const pending = items || []
        setInvitations(pending)
        setShowInvitations(pending.length > 0)
      })
      .catch((loadError) => {
        console.error('[AccountingOnboarding] Error loading invitations:', loadError)
      })
      .finally(() => setLoading(false))
  }, [])

  const openAccounting = async (unitId: string) => {
    await setUnitContext(unitId)
    await setCurrentUnit(userId, unitId)
    navigate('/accounting', { replace: true })
  }

  const handleAcceptInvitation = async (invitationId: string) => {
    setAccepting(true)
    setError(null)

    try {
      await acceptInvitation(invitationId)
      const units = await getUserUnitsByContext(userId, 'accounting')

      if (!units.length) {
        throw new Error(t('accounting.onboarding.errors.accept'))
      }

      await openAccounting(units[0].id)
    } catch (acceptError) {
      setError(acceptError instanceof Error ? acceptError.message : t('accounting.onboarding.errors.accept'))
      setAccepting(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const name = officeName.trim()

    if (name.length < 3) return

    setSaving(true)
    setError(null)

    try {
      const unit = await createUnit(name, createSlug(name), {
        context_key: 'accounting',
        attrs: { accounting_unit_type: 'office' },
      })

      await openAccounting(unit.id)
    } catch (saveError) {
      console.error('[AccountingOnboarding] Error creating office:', saveError)
      setError(saveError instanceof Error ? saveError.message : t('accounting.onboarding.errors.create'))
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50 p-4 dark:from-slate-950 dark:to-slate-900">
      <div className="w-full max-w-lg">
        <div className="rounded-2xl bg-white p-8 shadow-xl dark:bg-slate-800 md:p-10">
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {showInvitations ? (
            <InvitationsStep
              invitations={invitations}
              onAcceptInvitation={handleAcceptInvitation}
              onSkipInvitations={() => setShowInvitations(false)}
              isAccepting={accepting}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12v18H6zM9 7h6M9 11h6M9 15h2m2 0h2" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {t('accounting.onboarding.title')}
                </h1>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  {t('accounting.onboarding.subtitle')}
                </p>
              </div>

              <div>
                <label htmlFor="accounting-office-name" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('accounting.onboarding.name')}
                </label>
                <input
                  id="accounting-office-name"
                  autoFocus
                  required
                  minLength={3}
                  value={officeName}
                  onChange={(event) => setOfficeName(event.target.value)}
                  placeholder={t('accounting.onboarding.namePlaceholder')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
              </div>

              <button
                type="submit"
                disabled={saving || officeName.trim().length < 3}
                className="w-full rounded-lg bg-emerald-600 px-4 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? t('accounting.onboarding.creating') : t('accounting.onboarding.continue')}
              </button>
            </form>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-slate-500 dark:text-slate-400">
          {appConfig.name} © 2026
        </p>
      </div>
    </div>
  )
}
